<template>
  <section class="fe-wishlist" :class="{ 'fe-wishlist--embedded': embedded }">
    <!-- Standalone wishlist page uses .container; account layout parent already provides width -->
    <div :class="embedded ? 'fe-wishlist__inner fe-wishlist__inner--flush' : 'container'">
      <div v-if="wishlistStore.wishlists.length === 0" class="fe-wishlist__empty text-center py-5">
        <div class="fe-wishlist__empty-icon mb-3" aria-hidden="true">
          <i class="far fa-heart"></i>
        </div>
        <h2 class="fe-wishlist__empty-title">Your wishlist is empty</h2>
        <p class="fe-wishlist__empty-text">
          Tap the heart on any product in the shop to save it here.
        </p>
        <router-link to="/shop" class="fe-wishlist__btn">Continue shopping</router-link>
      </div>

      <div v-else>
        <div class="fe-wishlist__head mb-4">
          <h2 class="fe-wishlist__title">Saved products</h2>
          <span class="fe-wishlist__count">{{ wishlistStore.wishlists.length }} item(s)</span>
        </div>
        <div class="row g-4">
          <div
            v-for="item in wishlistStore.wishlists"
            :key="item.id"
            class="col-sm-6 col-lg-4"
          >
            <article class="fe-wish-card">
              <button
                type="button"
                class="fe-wish-card__remove"
                aria-label="Remove from wishlist"
                @click="wishlistStore.removeWishlist(item)"
              >
                <i class="fas fa-times"></i>
              </button>
              <router-link :to="`/product-details/${item.id}`" class="fe-wish-card__img-wrap">
                <img v-if="item.image" :src="item.image" :alt="item.title" class="fe-wish-card__img" />
                <span v-else class="fe-wish-card__ph"><i class="fas fa-image"></i></span>
              </router-link>
              <div class="fe-wish-card__body">
                <router-link :to="`/product-details/${item.id}`" class="fe-wish-card__name">
                  {{ item.title }}
                </router-link>
                <div class="fe-wish-card__price">
                  <span class="fe-wish-card__current">LKR {{ formatPrice(item.price) }}</span>
                  <template v-if="Number(item.discount_percentage) > 0">
                    <del class="fe-wish-card__orig">
                      LKR {{ formatPrice(originalPrice(item.price, item.discount_percentage)) }}
                    </del>
                  </template>
                </div>
                <p v-if="item.quantity === 0" class="fe-wish-card__stock text-danger mb-2">Out of stock</p>
                <div class="fe-wish-card__actions">
                  <button
                    v-if="!isInCart(item)"
                    type="button"
                    class="fe-wish-card__btn fe-wish-card__btn--primary"
                    :disabled="item.quantity === 0"
                    @click="cartStore.addCartProduct(item)"
                  >
                    Add to cart
                  </button>
                  <router-link v-else to="/cart" class="fe-wish-card__btn fe-wish-card__btn--outline">
                    View cart
                  </router-link>
                  <router-link :to="`/product-details/${item.id}`" class="fe-wish-card__btn fe-wish-card__btn--ghost">
                    View product
                  </router-link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useWishlistStore } from '@/stores/wishlistStore'
import { useCartStore } from '@/stores/useCartStore'
import { calculateOriginalPrice } from '@/utils/utils'

export default {
  name: 'WishlistArea',
  props: {
    /** When true, used inside My Account layout — tighter vertical spacing */
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      wishlistStore: useWishlistStore(),
      cartStore: useCartStore(),
    }
  },
  methods: {
    formatPrice(n) {
      return Number(n ?? 0).toFixed(2)
    },
    originalPrice(price, pct) {
      return calculateOriginalPrice(Number(price ?? 0), Number(pct ?? 0))
    },
    isInCart(product) {
      return this.cartStore.cart_products.some((p) => String(p.id) === String(product.id))
    },
  },
}
</script>

<style scoped>
.fe-wishlist {
  padding-top: 3.125rem;
  padding-bottom: 6.25rem;
}

.fe-wishlist.fe-wishlist--embedded {
  padding-top: 0;
  padding-bottom: 0;
}

.fe-wishlist.fe-wishlist--embedded .fe-wishlist__head {
  margin-top: 0;
}

.fe-wishlist__inner--flush {
  width: 100%;
  max-width: 100%;
  padding-left: 0;
  padding-right: 0;
}

.fe-wishlist__empty-icon {
  font-size: 3rem;
  color: #cbd5e1;
}
.fe-wishlist__empty-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
}
.fe-wishlist__empty-text {
  max-width: 420px;
  margin: 0 auto 1.25rem;
  color: #64748b;
  font-size: 1rem;
  line-height: 1.55;
}
.fe-wishlist__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.75rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  background: var(--fe-accent, #fcbe00);
  color: #fff;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}
.fe-wishlist__btn:hover {
  transform: translateY(-1px);
  color: #fff;
  box-shadow: 0 8px 22px rgba(252, 190, 0, 0.35);
}

.fe-wishlist__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.fe-wishlist__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  color: #0f172a;
}
.fe-wishlist__count {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
}

.fe-wish-card {
  position: relative;
  height: 100%;
  border: 1px solid #e8ecf1;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 6px 22px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.fe-wish-card__remove {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  color: #64748b;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  transition: color 0.15s ease, background 0.15s ease;
}
.fe-wish-card__remove:hover {
  color: #b91c1c;
  background: #fef2f2;
}
.fe-wish-card__img-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: #fafbfc;
  padding: 1rem;
}
.fe-wish-card__img {
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
}
.fe-wish-card__ph {
  font-size: 2.5rem;
  color: #cbd5e1;
}
.fe-wish-card__body {
  padding: 1rem 1.15rem 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
}
.fe-wish-card__name {
  font-weight: 700;
  font-size: 0.98rem;
  line-height: 1.35;
  color: #0f172a;
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.fe-wish-card__name:hover {
  color: var(--fe-secondary, #2563eb);
}
.fe-wish-card__price {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.65rem;
}
.fe-wish-card__current {
  font-weight: 800;
  font-size: 1.05rem;
  color: var(--fe-accent, #ea580c);
}
.fe-wish-card__orig {
  font-size: 0.85rem;
  color: #b91c1c;
}
.fe-wish-card__stock {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
}
.fe-wish-card__actions {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.5rem;
}
.fe-wish-card__btn {
  flex: 1 1 auto;
  min-width: 0;
  text-align: center;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.8rem;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.fe-wish-card__btn--primary {
  background: var(--fe-accent, #fcbe00);
  color: #fff;
  border-color: transparent;
}
.fe-wish-card__btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.fe-wish-card__btn--outline {
  border-color: #cbd5e1;
  background: #fff;
  color: #0f172a;
}
.fe-wish-card__btn--ghost {
  border-color: #e2e8f0;
  background: #f8fafc;
  color: #334155;
}
</style>
